import type { Router } from 'vue-router'
import { hasActiveModal, closeTopModal } from './useModalBackHandler'
import { useSwipeDrawer } from './useSwipeDrawer'

// Session-based in-app route stack
const appHistoryStack: string[] = []
let isHistoryGuardActive = false
let isPrimed = false

/**
 * Clean path without query params or hash
 */
export function cleanRoutePath(pathOrRoute: string): string {
  if (!pathOrRoute) return ''
  return pathOrRoute.split('?')[0].split('#')[0]
}

/**
 * Record route transitions within the SPA session
 */
export function recordAppNavigation(toPath: string, _fromPath?: string) {
  if (!toPath || toPath === '/') return

  if (appHistoryStack.length === 0) {
    appHistoryStack.push(toPath)
    return
  }

  const currentTop = appHistoryStack[appHistoryStack.length - 1]
  if (currentTop === toPath) return

  // If navigating back to previous entry, pop top
  if (appHistoryStack.length > 1 && appHistoryStack[appHistoryStack.length - 2] === toPath) {
    appHistoryStack.pop()
    return
  }

  // Push new route to stack
  appHistoryStack.push(toPath)
}

/**
 * Get the previous visited in-app route in the current session
 */
export function getInAppPreviousRoute(): string | null {
  if (appHistoryStack.length > 1) {
    return appHistoryStack[appHistoryStack.length - 2]
  }
  return null
}

/**
 * Get the overordinate (parent) route for a given path.
 * Returns null if the page is a top-level root page.
 */
export function getParentRoute(pathOrRoute: string): string | null {
  if (!pathOrRoute) return null
  const cleanPath = cleanRoutePath(pathOrRoute)

  // Sub-detail recipe views -> Kochbuch (/recipes)
  if (cleanPath.startsWith('/recipe/')) {
    if (cleanPath.endsWith('/print')) {
      const match = cleanPath.match(/^\/recipe\/([^/]+)\/print/)
      return match ? `/recipe/${match[1]}` : '/recipes'
    }
    return '/recipes'
  }

  if (cleanPath === '/recipe-categories') {
    return '/recipes'
  }

  if (cleanPath === '/article-categories' || cleanPath === '/categories' || cleanPath === '/units') {
    return '/articles'
  }

  if (cleanPath === '/articles') {
    return '/recipes'
  }

  if (cleanPath === '/settings') {
    const lastNonSettings = typeof localStorage !== 'undefined'
      ? localStorage.getItem('last_non_settings_route')
      : null
    return (lastNonSettings && lastNonSettings !== '/settings' && !lastNonSettings.startsWith('/settings'))
      ? lastNonSettings
      : '/recipes'
  }

  if (cleanPath === '/trash' || cleanPath === '/support' || cleanPath === '/user' || cleanPath === '/login') {
    return '/recipes'
  }

  // Top-level root pages (Kochbuch, Einkaufsliste) have no parent
  return null
}

/**
 * Navigates back according to the rule:
 * 1. Active modal -> close modal
 * 2. Active sidebar -> close sidebar
 * 3. Vorherige Seite (within session)
 * 4. Übergeordnete Seite (parent route)
 * 5. Sonst passiert gar nichts (app is NEVER closed)
 */
export function navigateAppBack(router?: Router): boolean {
  // 1. Modals
  if (hasActiveModal()) {
    return closeTopModal(true)
  }

  // 2. Sidebar
  try {
    const { isSidebarOpen, closeSidebar } = useSwipeDrawer()
    if (isSidebarOpen.value) {
      closeSidebar()
      return true
    }
  } catch {}

  const currentRoute = router?.currentRoute?.value
  const currentPath = currentRoute?.fullPath || (typeof window !== 'undefined' ? window.location.pathname : '')

  // 3. Vorherige Seite (im aktuellen Session-Verlauf)
  const prevRoute = getInAppPreviousRoute()
  if (prevRoute && prevRoute !== currentPath && prevRoute !== '/') {
    appHistoryStack.pop()
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back()
    } else {
      router?.push?.(prevRoute)?.catch?.(() => {})
    }
    return true
  }

  // 4. Übergeordnete Seite
  const parentRoute = getParentRoute(currentPath)
  if (parentRoute && parentRoute !== currentPath) {
    appHistoryStack.length = 0
    appHistoryStack.push(parentRoute)
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back()
    } else {
      router?.replace?.(parentRoute)?.catch?.(() => {})
    }
    return true
  }

  // 5. Sonst passiert gar nichts
  return false
}

/**
 * Prime the browser history stack on initial launch:
 * If app opens directly on a subpage (e.g. /recipe/:id), we inject the parent route (/recipes)
 * into the history before the subpage so that the mobile hardware Back button naturally
 * navigates back to /recipes without closing the app.
 * Also establishes a baseline guard to trap Back presses at the root.
 */
export function primeAppHistory(router: Router) {
  if (typeof window === 'undefined' || !window.history || !window.history.pushState) return
  if (isPrimed) return
  isPrimed = true

  const currentRoute = router?.currentRoute?.value
  if (!currentRoute) return
  const currentPath = currentRoute.fullPath || window.location.pathname
  if (!currentPath || currentPath === '/') return

  const parentRoute = getParentRoute(currentPath)
  const state = window.history.state || {}
  const hasHistoryBack = Boolean(state.back)
  const isAtRootPosition = state.position === 0 || typeof state.position !== 'number'

  // If directly opened on a subpage (e.g. /recipe/rec-xyz):
  if (parentRoute && parentRoute !== currentPath && (!hasHistoryBack || isAtRootPosition)) {
    try {
      // 0: Guard
      window.history.replaceState({
        __appGuard: true,
        current: parentRoute,
        back: null,
        forward: parentRoute,
        position: 0
      }, '', parentRoute)

      // 1: Parent route (/recipes)
      window.history.pushState({
        current: parentRoute,
        back: parentRoute,
        forward: currentPath,
        position: 1
      }, '', parentRoute)

      // 2: Current subpage
      window.history.pushState({
        current: currentPath,
        back: parentRoute,
        forward: null,
        position: 2
      }, '', currentPath)

      appHistoryStack.length = 0
      appHistoryStack.push(parentRoute, currentPath)
      return
    } catch (e) {
      console.warn('[useAppNavigation] Failed to prime subpage history:', e)
    }
  }

  // If directly opened on root page (e.g. /recipes or /shopping-list) at position 0:
  if (!hasHistoryBack || isAtRootPosition) {
    try {
      window.history.replaceState({
        __appGuard: true,
        current: currentPath,
        back: null,
        forward: currentPath,
        position: 0
      }, '', currentPath)

      window.history.pushState({
        current: currentPath,
        back: currentPath,
        forward: null,
        position: 1
      }, '', currentPath)

      appHistoryStack.length = 0
      appHistoryStack.push(currentPath)
    } catch (e) {
      console.warn('[useAppNavigation] Failed to prime root history:', e)
    }
  }
}

/**
 * Initialize history guard to intercept mobile hardware / browser Back gestures
 */
export function initHistoryGuard(router: Router) {
  if (typeof window === 'undefined' || !window.history || !window.history.pushState) return
  if (isHistoryGuardActive) return
  isHistoryGuardActive = true

  // Listen on capturing phase
  window.addEventListener('popstate', (event) => {
    // 1. Modals
    if (hasActiveModal()) {
      return
    }

    // 2. Sidebar
    try {
      const { isSidebarOpen, closeSidebar } = useSwipeDrawer()
      if (isSidebarOpen.value) {
        event.stopImmediatePropagation()
        closeSidebar()
        try {
          window.history.pushState(event.state || {}, '')
        } catch {}
        return
      }
    } catch {}

    // 3. Guard entry reached (root of history reached on mobile!)
    if (event.state?.__appGuard) {
      event.stopImmediatePropagation()
      const currentRoute = router?.currentRoute?.value
      const currentPath = currentRoute?.fullPath || window.location.pathname
      const parentRoute = getParentRoute(currentPath)

      if (parentRoute && parentRoute !== currentPath) {
        // If on a subpage, navigate to parent route
        router.replace(parentRoute).catch(() => {})
        try {
          window.history.pushState({
            current: parentRoute,
            back: null,
            forward: null,
            position: 1
          }, '', parentRoute)
        } catch {}
      } else {
        // Root page: "sonst passiert gar nichts" - app stays open!
        try {
          window.history.pushState({
            current: currentPath,
            back: null,
            forward: null,
            position: 1
          }, '', currentPath)
        } catch {}
      }
      return
    }

    // 4. Normal navigation: keep stack in sync
    if (appHistoryStack.length > 1) {
      appHistoryStack.pop()
    }
  }, true)
}

/**
 * For testing purposes: resets in-app history stack
 */
export function resetAppHistoryStackForTesting() {
  appHistoryStack.length = 0
  isHistoryGuardActive = false
  isPrimed = false
}
